import React, { useState } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

const GrowthChart = ({ data = [], activeMembers = 0, isCurrentYear = true }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    const currentYear = new Date().getFullYear();
    const previousYear = currentYear - 1;

    const rawData = data.length > 0 ? data : [];
    const hasHistory = rawData.some(d => d.currentActive > 0 || d.prevActive > 0);
    const safeData = hasHistory ? rawData : [];

    const maxActive = Math.max(10, activeMembers, ...safeData.map(d => Math.max(d.currentActive || 0, d.prevActive || 0))) * 1.2;

    const currentActivePoints = safeData.map((item, idx) => {
        return {
            x: (100 / Math.max(1, safeData.length)) * (idx + 0.5),
            y: 100 - (item.currentActive / maxActive) * 100
        };
    });

    const prevActivePoints = safeData.map((item, idx) => {
        return {
            x: (100 / Math.max(1, safeData.length)) * (idx + 0.5),
            y: 100 - (item.prevActive / maxActive) * 100
        };
    });

    const currentMonthIdx = new Date().getMonth();
    const activePointsToDraw = isCurrentYear 
        ? currentActivePoints.slice(0, currentMonthIdx + 1)
        : currentActivePoints;

    const buildSmoothPath = (pts) => {
        if (pts.length === 0) return "";
        if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
        const tension = 0.3;

        let path = `M ${pts[0].x} ${pts[0].y}`;

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[Math.min(pts.length - 1, i + 2)];

            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;
            const cp2x_val = p2.x;
            const cp2y_val = p2.y;

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${cp2x_val} ${cp2y_val}`;
        }

        return path;
    };

    const currentActiveSmoothPath = buildSmoothPath(activePointsToDraw);
    const prevActiveSmoothPath = buildSmoothPath(prevActivePoints);

    return (
        <Card 
            className="neo-stat-card h-100" 
            style={{ 
                borderRadius: "16px", 
                border: "2px solid #482712", 
                boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                background: "#fff",
                fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
        >
            <CardHeader className="neo-chart-card-header">
                <div className="neo-chart-header-container">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{ 
                                width: "38px", 
                                height: "38px", 
                                minWidth: "38px",
                                borderRadius: "10px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)",
                                color: "#e2661c",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <i className="mdi mdi-trending-up font-size-18"></i>
                        </div>
                        <h5 className="neo-chart-title">
                            Clientes Ativos
                        </h5>
                    </div>
                    <div className="neo-chart-legend-container">
                        <div className="d-flex align-items-center">
                            <span className="rounded-circle" style={{ width: "10px", height: "10px", backgroundColor: "#ebdcc5", border: "1px solid rgba(72, 39, 18, 0.15)", marginRight: "6px" }} />
                            <small style={{ color: "#784b22", fontWeight: "800", fontSize: "0.72rem" }}>{previousYear}</small>
                        </div>
                        <div className="d-flex align-items-center ms-2">
                            <span className="rounded-circle" style={{ width: "10px", height: "10px", backgroundColor: "#e2661c", marginRight: "6px" }} />
                            <small style={{ color: "#784b22", fontWeight: "800", fontSize: "0.72rem" }}>{currentYear}</small>
                        </div>
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-4 mt-2">
                <div style={{ overflowX: "auto", overflowY: "hidden", paddingBottom: "10px" }} className="custom-scroll">
                    <div style={{ height: "260px", display: "flex", alignItems: "flex-end" }}>
                        <div style={{ height: "200px", position: "relative", paddingLeft: "10px", paddingRight: "10px", minWidth: "550px", width: "100%" }}>

                        {safeData.length === 0 ? (
                            <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                                <i className="mdi mdi-chart-box-outline font-size-24 mb-2 opacity-50" style={{ color: "#784b22" }} />
                                <span className="font-size-12 fw-bold opacity-75" style={{ color: "#784b22" }}>Coletando primeiro registro histórico...</span>
                            </div>
                        ) : (
                            <div className="h-100 w-100 d-flex position-relative align-items-end" style={{ zIndex: 1, paddingBottom: "35px", gap: "10px" }}>
                                
                                {/* O SVG Interativo para Linhas e Curvas */}
                                <svg 
                                    style={{ 
                                        position: "absolute", 
                                        top: 0, 
                                        left: 0, 
                                        width: "100%", 
                                        height: "calc(100% - 35px)", 
                                        pointerEvents: "none",
                                        zIndex: 4 
                                    }}
                                    preserveAspectRatio="none"
                                    viewBox="0 0 100 100"
                                >
                                    <defs>
                                        <filter id="activeShadow" x="-20%" y="-20%" width="140%" height="140%">
                                            <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="#e2661c" floodOpacity="0.4" />
                                        </filter>
                                    </defs>
                                    {/* Curva Ano Anterior */}
                                    <path
                                        d={prevActiveSmoothPath}
                                        fill="none"
                                        stroke="#ebdcc5"
                                        strokeWidth="3.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                    {/* Curva Ano Atual */}
                                    <path
                                        d={currentActiveSmoothPath}
                                        fill="none"
                                        stroke="#e2661c"
                                        strokeWidth="3.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                        filter="url(#activeShadow)"
                                    />
                                </svg>
                                
                                {/* Eixo X interativo de Colunas Invisíveis para Trigger de Hover */}
                                {safeData.map((item, idx) => {
                                    const isHovered = hoveredIdx === idx;
                                    const currentY = currentActivePoints[idx] ? 100 - currentActivePoints[idx].y : 0;
                                    const prevY = prevActivePoints[idx] ? 100 - prevActivePoints[idx].y : 0;
                                
                                    return (
                                        <div 
                                            key={idx} 
                                            className="flex-grow-1 h-100 d-flex flex-column align-items-center justify-content-end position-relative"
                                            onMouseEnter={() => setHoveredIdx(idx)}
                                            onMouseLeave={() => setHoveredIdx(null)}
                                            style={{ cursor: "crosshair", zIndex: 5 }}
                                        >
                                            {/* Linha Vertical Tracejada ao Hover */}
                                            {isHovered && (
                                                <div 
                                                    className="position-absolute"
                                                    style={{ 
                                                        height: "calc(100% - 35px)", 
                                                        width: "1.5px", 
                                                        borderLeft: "1.5px dashed rgba(72, 39, 18, 0.2)", 
                                                        bottom: "35px",
                                                        left: "50%",
                                                        transform: "translateX(-50%)",
                                                        pointerEvents: "none",
                                                        zIndex: 0 
                                                    }}
                                                />
                                            )}                                            {/* Pontos de Destaque Flutuantes sobre as Curvas */}
                                            {isHovered && currentActivePoints[idx] && (!isCurrentYear || idx <= currentMonthIdx) && (
                                                <div
                                                    className="position-absolute shadow"
                                                    style={{
                                                        bottom: `calc(${currentY}% * (1 - 35px / 100%) + 35px)`,
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        left: "50%",
                                                        backgroundColor: "#fff",
                                                        border: "3.5px solid #e2661c",
                                                        transform: "translate(-50%, 50%)",
                                                        zIndex: 20,
                                                        pointerEvents: "none"
                                                    }}
                                                />
                                            )}
                                            {isHovered && prevActivePoints[idx] && (
                                                <div
                                                    className="position-absolute shadow"
                                                    style={{
                                                        bottom: `calc(${prevY}% * (1 - 35px / 100%) + 35px)`,
                                                        width: "12px",
                                                        height: "12px",
                                                        borderRadius: "50%",
                                                        left: "50%",
                                                        backgroundColor: "#fff",
                                                        border: "3.5px solid #ebdcc5",
                                                        transform: "translate(-50%, 50%)",
                                                        zIndex: 20,
                                                        pointerEvents: "none"
                                                    }}
                                                />
                                            )}
 
                                            {/* Card Tooltip suspenso */}
                                            {isHovered && (
                                                <div 
                                                    className="position-absolute bg-white shadow-lg rounded-3 p-3 text-start"
                                                    style={{ 
                                                        top: "-48px", 
                                                        width: "180px",
                                                        left: "50%", 
                                                        transform: "translateX(-50%)",
                                                        pointerEvents: "none",
                                                        zIndex: 9999,
                                                        border: "2px solid #482712"
                                                    }}
                                                >
                                                    <h6 className="fw-bold mb-3 pb-2 border-bottom text-center text-uppercase font-size-12" style={{ color: "#482712" }}>{item.month}</h6>
                                                    {(!isCurrentYear || idx <= currentMonthIdx) && (
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="text-muted small d-flex align-items-center">
                                                                <span className="rounded-circle shadow-sm me-2" style={{ width: "8px", height: "8px", backgroundColor: "#e2661c" }} />
                                                                Ativos Atual
                                                            </span>
                                                            <span className="fw-bold font-size-13 text-dark">{item.currentActive}</span>
                                                        </div>
                                                    )}
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="text-muted small d-flex align-items-center">
                                                            <span className="rounded-circle shadow-sm me-2" style={{ width: "8px", height: "8px", backgroundColor: "#ebdcc5", border: "1px solid rgba(72, 39, 18, 0.15)" }} />
                                                            Ano Ant.
                                                        </span>
                                                        <span className="fw-bold font-size-13 text-secondary">{item.prevActive}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Mês no eixo X */}
                                            <span className={`position-absolute fw-bold transition-all`} style={{ bottom: "-25px", fontSize: "10px", color: isHovered ? "#e2661c" : "#482712" }}>{item.month}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </CardBody>
        </Card>
    );
};

export default GrowthChart;
